import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import StaffDashboard from "./pages/StaffDashboard";
import ApproverDashboard from "./pages/ApproverDashboard";
import SecurityPortal from "./pages/SecurityPortal";
import AdminPortal from "./pages/AdminPortal";
import ChangePasswordModal from "./components/ChangePasswordModal";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-primary font-heading text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "security") return <Navigate to="/security" replace />;
    if (user.role === "hod" || user.role === "hr" || user.is_approver) return <Navigate to="/approvals" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, mustChangePassword } = useAuth();

  return (
    <>
      {user && mustChangePassword && <ChangePasswordModal />}
      <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["staff", "hod", "hr"]}>
            <StaffDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/approvals"
        element={
          <ProtectedRoute allowedRoles={["hod", "hr", "admin"]}>
            <ApproverDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/security"
        element={
          <ProtectedRoute allowedRoles={["security", "admin"]}>
            <SecurityPortal />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminPortal />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            {user?.role === "admin" ? (
              <Navigate to="/admin" replace />
            ) : user?.role === "security" ? (
              <Navigate to="/security" replace />
            ) : user?.role === "hod" || user?.role === "hr" || user?.is_approver ? (
              <Navigate to="/approvals" replace />
            ) : (
              <Navigate to="/dashboard" replace />
            )}
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            className: "font-body",
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
